import * as React from 'react';

import Divider from '@mui/material/Divider';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { useClientFields } from '../../../../sic-lib/api/hooks/useClientFields';
import { useGetViews } from '../../../../sic-lib/api/hooks/useViews';
import { CONTACTS_DEFAULT_SORT, CONTACTS_SORTABLE_FIELDS } from '../../../../sic-lib/defaults';
import { ContactsListSortDirection } from '../../../../sic-lib/enums/ContactsListSortDirection';
import { ClientField } from '../../../../sic-lib/types/ClientField';
import {
    hasMatchingFieldId,
    hasMatchingValidationType,
    hasMatchingViewId,
} from '../../../../sic-lib/utils/functionalFilters';
import { mapToListOfFieldIds } from '../../../../sic-lib/utils/functionalMappers';
import { requestContacts, setSort } from '../../../../store/conversationGrid';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';

interface Props {
    anchorEl: HTMLElement | null;
    menuId: string;
    viewId: number;
    onCloseHandler: () => void;
}

export default function MenuContactsSortRaw({ anchorEl, menuId, viewId, onCloseHandler }: Props) {
    const dispatch = useAppDispatch();

    const { data } = useClientFields();

    const clientFields = React.useMemo(() => {
        if (!data) {
            return [];
        }
        return data;
    }, [data]);

    const { data: viewsData } = useGetViews();

    const views = React.useMemo(() => {
        return viewsData || [];
    }, [viewsData]);

    const contactSort = useAppSelector(state => state.conversationGrid.sortField);

    const [sortOptions, setSortOptions] = React.useState<ClientField[]>([]);

    React.useEffect(() => {
        const view = views.filter(hasMatchingViewId(viewId))[0];

        if (view) {
            /*
                TIP: firstname (fieldId: 13) and lastname (fieldId: 14) should be included in all sort menus
             */
            const sortFields = [13, 14, 9, 18, ...view.properties.map(mapToListOfFieldIds)].filter(
                ele => ele !== 1,
            );

            const options = clientFields
                .filter(hasMatchingValidationType(CONTACTS_SORTABLE_FIELDS))
                .filter(hasMatchingFieldId(sortFields));
            setSortOptions(options);
        }
    }, [viewId, clientFields, views]);

    const handleSelect = (event: React.MouseEvent<HTMLElement>) => {
        const { fieldId, direction } = event.currentTarget.dataset;

        if (fieldId && direction) {
            dispatch(setSort({ sortField: +fieldId, sortDirection: +direction }));
            dispatch(requestContacts());

            onCloseHandler();
        }
    };

    return (
        <Menu id={menuId} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={onCloseHandler}>
            {sortOptions.map(itm => {
                return (
                    <div key={itm.fieldId}>
                        <Divider />
                        <MenuItem
                            data-field-id={itm.fieldId}
                            data-direction={ContactsListSortDirection.descending}
                            selected={
                                contactSort.fieldId === itm.fieldId &&
                                contactSort.direction === ContactsListSortDirection.descending
                            }
                            onClick={handleSelect}
                        >
                            {`${itm.label} Desc`}
                        </MenuItem>
                        <MenuItem
                            data-field-id={itm.fieldId}
                            data-direction={ContactsListSortDirection.ascending}
                            selected={
                                contactSort.fieldId === itm.fieldId &&
                                contactSort.direction === ContactsListSortDirection.ascending
                            }
                            onClick={handleSelect}
                        >
                            {`${itm.label} Asc`}
                        </MenuItem>
                    </div>
                );
            })}
            <Divider />
            <MenuItem
                data-field-id={CONTACTS_DEFAULT_SORT.fieldId}
                data-direction={CONTACTS_DEFAULT_SORT.direction}
                onClick={handleSelect}
            >
                Clear Sort
            </MenuItem>
        </Menu>
    );
}
